import { useState, useEffect, useMemo } from 'react'
import { Tag, Table, Empty, Spin } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { getStockValuationHistory, getStockAnalyst } from '@/api'
import type { ValuationDetail, AnalystConsensus } from '@/api'
import './index.scss'

interface ValuationCard {
    title: string
    value: number | null
    average: number | null
    plate: number | null
    percentile: number | null
}

function percentileTag(pct: number | null): { label: string; cls: string } {
    if (pct == null) return { label: '--', cls: 'is-default' }
    if (pct < 30) return { label: '低估', cls: 'is-success' }
    if (pct <= 70) return { label: '合理区间', cls: 'is-warning' }
    return { label: '偏高', cls: 'is-error' }
}

interface TargetRow {
    id: number
    method: string
    targetPrice: number | null
    potentialSpace: number | null
    basis: string
}

export default function ValuationAnchor({ stockCode, currentPrice }: {
    stockCode: string
    currentPrice: number | null
}) {
    const [valDetail, setValDetail] = useState<ValuationDetail | null>(null)
    const [consensus, setConsensus] = useState<AnalystConsensus | null>(null)
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        setLoading(true)
        Promise.allSettled([
            getStockValuationHistory(stockCode, 1),
            getStockAnalyst(stockCode),
        ]).then((results) => {
            if (results[0].status === 'fulfilled') setValDetail(results[0].value)
            if (results[1].status === 'fulfilled') setConsensus(results[1].value)
            setLoading(false)
        })
    }, [stockCode])

    // historical_items 按 valuation_date DESC（最新在前）；trend 区间统计为 PE 口径。
    const trend = valDetail?.trend ?? null
    const latest = trend?.historical_items?.[0] ?? null

    // pe_ttm / pb_mrq / ps_ttm 三列均在最新一条 historical_item 上；
    // average / plate / percentile 取自 trend 的 PE 区间统计（PB/PS 无统计 → 显示 --）。
    const cards: ValuationCard[] = [
        {
            title: '市盈率 PE（TTM）',
            value: latest?.pe_ttm ?? null,
            average: trend?.average_value ?? null,
            plate: latest?.plate_value ?? null,
            percentile: trend?.valuation_percentile ?? null,
        },
        {
            title: '市净率 PB（MRQ）',
            value: latest?.pb_mrq ?? null,
            average: null,
            plate: null,
            percentile: null,
        },
        {
            title: '市销率 PS（TTM）',
            value: latest?.ps_ttm ?? null,
            average: null,
            plate: null,
            percentile: null,
        },
    ]

    const fmt = (v: number | null, suffix = 'x') => {
        if (v == null) return '--'
        return `${v.toFixed(2)}${suffix}`
    }

    const targetRows: TargetRow[] = useMemo(() => {
        if (!consensus) return []
        const space = (target: number | null) => {
            if (target == null || !currentPrice || currentPrice <= 0) return null
            return ((target - currentPrice) / currentPrice) * 100
        }
        const basis = `${consensus.total_analysts ?? 0}位分析师`
        return [
            { id: 1, method: '分析师一致预期', targetPrice: consensus.target_price_avg, potentialSpace: space(consensus.target_price_avg), basis },
            { id: 2, method: '分析师最高', targetPrice: consensus.target_price_high, potentialSpace: space(consensus.target_price_high), basis },
            { id: 3, method: '分析师最低', targetPrice: consensus.target_price_low, potentialSpace: space(consensus.target_price_low), basis },
        ]
    }, [consensus, currentPrice])

    const columns: ColumnsType<TargetRow> = [
        { title: '方法', dataIndex: 'method', key: 'method',
            render: (text: string) => <span style={{ color: '#fff' }}>{text}</span> },
        { title: '目标价', dataIndex: 'targetPrice', key: 'targetPrice',
            render: (v: number | null) => v != null
                ? <span className="green" style={{ fontWeight: 700 }}>￥{v.toFixed(2)}</span>
                : <span className="color-3">--</span> },
        { title: '潜在空间', dataIndex: 'potentialSpace', key: 'potentialSpace',
            render: (v: number | null) => v != null
                ? <span className={`${v > 0 ? 'green' : 'red'}`} style={{ fontWeight: 700 }}>{v > 0 ? '+' : ''}{v.toFixed(2)}%</span>
                : <span className="color-3">--</span> },
        { title: '依据', dataIndex: 'basis', key: 'basis' },
    ]

    return (
        <div className="valuation-anchor">
            <Spin spinning={loading}>
                <div className="valuation-anchor__body">
                    <div className="valuation-anchor__title">当前估值水平</div>
                    <div className="valuation-anchor__content">
                        {cards.map((card, idx) => {
                            const tag = percentileTag(card.percentile)
                            return (
                                <div className="valuation-anchor__content-item" key={idx}>
                                    <div className="valuation-anchor__content-item-title">{card.title}</div>
                                    <div className="valuation-anchor__content-item-value">
                                        {card.value != null ? card.value.toFixed(2) : '--'}
                                        <i className="unit text-10">x</i>
                                    </div>
                                    <div className="valuation-anchor__content-item-unit text-10">
                                        历史均值 {fmt(card.average)}{card.plate != null ? ` | 行业均值 ${fmt(card.plate)}` : ''}
                                    </div>
                                    <Tag className={`valuation-anchor__content-item-percent text-10 ${tag.cls}`}>
                                        {tag.label}{card.percentile != null ? ` ${card.percentile.toFixed(0)}%分位` : ''}
                                    </Tag>
                                </div>
                            )
                        })}
                    </div>
                    <div className="valuation-anchor__title">目标价测算</div>
                    {targetRows.length > 0 ? (
                        <Table
                            rowKey="id"
                            dataSource={targetRows}
                            columns={columns}
                            pagination={false}
                            size="small"
                            className="valuation-anchor__table"
                        />
                    ) : (
                        <div style={{ padding: '24px 0' }}>
                            <Empty description="暂无分析师数据" />
                        </div>
                    )}
                </div>
            </Spin>
        </div>
    )
}
